import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import {Button, Container, Spinner} from 'react-bootstrap';
import {FaBell, FaComments, FaPaperPlane, FaTrashAlt} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import CommonMetaTag from './common-meta-tag';
import {deleteNotification, deleteReadNotifications, getBookingMessages, getMyOpenMatchByPublicId, getNotifications, getOpenMatchMessages, readAllNotifications, readNotification, sendBookingMessage, sendOpenMatchMessage} from '../api';
import WebPushSettings from './web-push-settings';

const CommunicationsCenter = ({token, profile, history, location}) => {
  const {t, i18n} = useTranslation();
  const bookingId = new URLSearchParams(location.search).get('booking');
  const openMatchId = new URLSearchParams(location.search).get('openMatch');
  const openMatchPublicId = new URLSearchParams(location.search).get('openMatchPublicId');
  const [resolvedOpenMatchId, setResolvedOpenMatchId] = useState(openMatchId || null);
  const chatType = openMatchId || openMatchPublicId || resolvedOpenMatchId ? 'openMatch' : bookingId ? 'booking' : null;
  const chatId = resolvedOpenMatchId || bookingId;
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const loadNotifications = useCallback(async () => {
    const response = await getNotifications(token, 100);
    if (response.status === 200) setNotifications(response.json.items || []);
  }, [token]);
  const loadMessages = useCallback(async () => {
    if (!chatId) return;
    const response = chatType === 'openMatch'
      ? await getOpenMatchMessages(chatId, token)
      : await getBookingMessages(chatId, token);
    if (response.status === 200) setMessages(response.json || []);
    else setError(t(chatType === 'openMatch' ? 'COMMUNICATIONS_OPEN_MATCH_LOAD_ERROR' : 'COMMUNICATIONS_BOOKING_LOAD_ERROR'));
  }, [chatId, chatType, token, t]);
  const resolveOpenMatchPublicId = useCallback(async () => {
    if (!openMatchPublicId || resolvedOpenMatchId || !token) return;
    const response = await getMyOpenMatchByPublicId(openMatchPublicId, token);
    if (response.status === 200 && response.json.openMatch && response.json.openMatch.id) {
      setResolvedOpenMatchId(String(response.json.openMatch.id));
    } else {
      setError(t('COMMUNICATIONS_OPEN_MATCH_LOAD_ERROR'));
    }
  }, [openMatchPublicId, resolvedOpenMatchId, token, t]);
  useEffect(() => {
    if (openMatchId && openMatchId !== resolvedOpenMatchId) {
      setResolvedOpenMatchId(openMatchId);
    }
  }, [openMatchId, resolvedOpenMatchId]);
  useEffect(() => {
    setLoading(true);
    Promise.all([resolveOpenMatchPublicId(), loadNotifications(), loadMessages()]).finally(() => setLoading(false));
  }, [loadMessages, loadNotifications, resolveOpenMatchPublicId]);

  if (!token) return <Redirect to="/login"/>;
  const openNotification = item => async () => {
    if (!item.read) await readNotification(item.id, token);
    if (item.actionUrl) history.push(item.actionUrl);
    loadNotifications();
  };
  const markAll = async () => { await readAllNotifications(token); loadNotifications(); };
  const removeNotification = item => async event => {
    event.stopPropagation();
    if (!window.confirm(t('COMMUNICATIONS_DELETE_CONFIRM', {title: item.title}))) return;
    setDeletingId(item.id);
    const response = await deleteNotification(item.id, token);
    setDeletingId(null);
    if (response.status === 200) setNotifications(current => current.filter(value => value.id !== item.id));
    else setError(t('COMMUNICATIONS_DELETE_ERROR'));
  };
  const removeRead = async () => {
    if (!window.confirm(t('COMMUNICATIONS_DELETE_READ_CONFIRM'))) return;
    const response = await deleteReadNotifications(token);
    if (response.status === 200) setNotifications(current => current.filter(item => !item.read));
    else setError(t('COMMUNICATIONS_DELETE_READ_ERROR'));
  };
  const submit = async event => {
    event.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    const response = chatType === 'openMatch'
      ? await sendOpenMatchMessage(chatId, body, token)
      : await sendBookingMessage(chatId, body, token);
    setSending(false);
    if (response.status === 201) { setBody(''); loadMessages(); }
    else setError(t('COMMUNICATIONS_SEND_ERROR'));
  };

  return <>
    <CommonMetaTag/>
    <Container className="communications-skywork">
      <header><small>{t('COMMUNICATIONS_KICKER')}</small><h1>{t('COMMUNICATIONS_TITLE')}</h1><p>{t('COMMUNICATIONS_INTRO')}</p></header>
      {chatId ? <section className="communications-skywork__chat">
        <div className="communications-skywork__section-head"><span><FaComments/><strong>{t(chatType === 'openMatch' ? 'COMMUNICATIONS_MATCH_CHAT' : 'COMMUNICATIONS_BOOKING_CHAT', {id: chatId})}</strong></span><button type="button" onClick={() => history.push('/messages')}>{t('COMMUNICATIONS_BACK')}</button></div>
        {error ? <p className="communications-skywork__error">{error}</p> : null}
        <div className="communications-skywork__messages">
          {loading ? <Spinner animation="border"/> : messages.length ? messages.map(message => <div key={message.id} className={profile && message.senderUid === profile.uid ? 'is-mine' : ''}><strong>{message.senderName}</strong><p>{message.body}</p><small>{new Date(message.createdAt).toLocaleString(i18n.language)}</small></div>) : <p>{t(chatType === 'openMatch' ? 'COMMUNICATIONS_EMPTY_MATCH_CHAT' : 'COMMUNICATIONS_EMPTY_BOOKING_CHAT')}</p>}
        </div>
        <form onSubmit={submit}><textarea value={body} maxLength="2000" onChange={event => setBody(event.target.value)} placeholder={t(chatType === 'openMatch' ? 'COMMUNICATIONS_MATCH_PLACEHOLDER' : 'COMMUNICATIONS_BOOKING_PLACEHOLDER')}/><Button type="submit" disabled={!body.trim() || sending}><FaPaperPlane/>{t(sending ? 'COMMUNICATIONS_SENDING' : 'COMMUNICATIONS_SEND')}</Button></form>
      </section> : <section className="communications-skywork__notifications">
        <div className="communications-skywork__section-head"><span><FaBell/><strong>{t('COMMUNICATIONS_YOUR_NOTIFICATIONS')}</strong></span><div className="communications-skywork__notification-actions"><button type="button" onClick={markAll}>{t('COMMUNICATIONS_MARK_ALL_READ')}</button>{notifications.some(item => item.read) ? <button type="button" className="is-delete" onClick={removeRead}><FaTrashAlt/> {t('COMMUNICATIONS_DELETE_READ')}</button> : null}</div></div>
        <WebPushSettings token={token}/>
        {error ? <p className="communications-skywork__error">{error}</p> : null}
        {loading ? <Spinner animation="border"/> : notifications.length ? notifications.map(item => <div key={item.id} className={`communications-skywork__notification${item.read ? '' : ' is-unread'}`}><button type="button" className="communications-skywork__notification-content" onClick={openNotification(item)}><span><strong>{item.title}</strong><small>{item.body}</small></span><time>{new Date(item.createdAt).toLocaleString(i18n.language)}</time></button><button type="button" className="communications-skywork__notification-delete" aria-label={t('COMMUNICATIONS_DELETE_ARIA', {title: item.title})} title={t('COMMUNICATIONS_DELETE')} disabled={deletingId === item.id} onClick={removeNotification(item)}>{deletingId === item.id ? <Spinner animation="border" size="sm"/> : <FaTrashAlt/>}</button></div>) : <p>{t('COMMUNICATIONS_EMPTY_NOTIFICATIONS')}</p>}
      </section>}
    </Container>
  </>;
};
CommunicationsCenter.propTypes = {token: PropTypes.string, profile: PropTypes.object, history: PropTypes.object.isRequired, location: PropTypes.object.isRequired};
CommunicationsCenter.defaultProps = {token: null, profile: null};
export default withRouter(connect(({login: {token, profile}}) => ({token, profile}))(CommunicationsCenter));
